import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { EMPTY, catchError, finalize, take, tap } from 'rxjs';
import { MfaHttpService } from '@core/http/mfa.http.service';
import { MfaChallengeAction, MfaInitiateOpts, MfaValidateOpts } from '@interfaces';

type EnableMfaStep = 'PASSWORD' | 'CODE' | 'RESULT';

export type EnableMfaModalMode = 'enable' | 'disable';

export interface EnableMfaModalData {
  mode: EnableMfaModalMode;
  phoneNumber?: string | null;
}

export interface EnableMfaModalResult {
  success?: boolean;
  mfaEnabled?: boolean;
}

@Component({
  selector: 'app-enable-mfa-modal',
  templateUrl: './enable-mfa-modal.component.html',
  styleUrls: ['./enable-mfa-modal.component.scss'],
  standalone: false,
})
export class EnableMfaModalComponent {
  public readonly data = inject<EnableMfaModalData>(MAT_DIALOG_DATA);
  public readonly dialogRef = inject(MatDialogRef<EnableMfaModalComponent, EnableMfaModalResult>);
  private readonly fb = inject(FormBuilder);
  private readonly mfaHttpService = inject(MfaHttpService);

  public isPasswordVisible = false;
  public isEnableMode = false;
  public currentStep: EnableMfaStep = 'PASSWORD';
  public loading = false;
  public passwordErrorMessageKey: string | null = null;
  public codeErrorMessageKey: string | null = null;
  public resultTitleKey: string | null = null;
  public resultDescriptionKey: string | null = null;
  public resultSecondaryDescriptionKey: string | null = null;
  public resultMfaEnabled: boolean | null = null;
  public actionTitleKey = 'ENABLE_MFA_MODAL.ENABLE_ACTION';
  public passwordStepDescriptionKey = 'ENABLE_MFA_MODAL.PASSWORD_STEP.ENABLE_DESCRIPTION';
  public codeStepDescriptionKey = 'ENABLE_MFA_MODAL.CODE_STEP.ENABLE_DESCRIPTION';

  public passwordForm: FormGroup<any> = this.fb.group({
    password: [null, Validators.compose([Validators.required, Validators.minLength(6), Validators.maxLength(100)])],
  });

  public codeForm: FormGroup<any> = this.fb.group({
    code: [null, Validators.compose([Validators.required, Validators.pattern(/^\d{6}$/)])],
  });

  constructor() {
    this.isEnableMode = this.data.mode === 'enable';
    this.currentStep = 'PASSWORD';

    if (this.isEnableMode) {
      this.actionTitleKey = 'ENABLE_MFA_MODAL.ENABLE_ACTION';
      this.passwordStepDescriptionKey = 'ENABLE_MFA_MODAL.PASSWORD_STEP.ENABLE_DESCRIPTION';
      this.codeStepDescriptionKey = 'ENABLE_MFA_MODAL.CODE_STEP.ENABLE_DESCRIPTION';
      return;
    }

    this.actionTitleKey = 'ENABLE_MFA_MODAL.DISABLE_ACTION';
    this.passwordStepDescriptionKey = 'ENABLE_MFA_MODAL.PASSWORD_STEP.DISABLE_DESCRIPTION';
    this.codeStepDescriptionKey = 'ENABLE_MFA_MODAL.CODE_STEP.DISABLE_DESCRIPTION';
  }

  public close(): void {
    if (this.loading) {
      return;
    }

    if (this.currentStep === 'RESULT' && this.resultMfaEnabled !== null) {
      this.dialogRef.close({ success: true, mfaEnabled: this.resultMfaEnabled });
      return;
    }

    this.dialogRef.close();
  }

  public confirm(): void {
    if (this.currentStep === 'PASSWORD') {
      this.submitPassword();
      return;
    }

    if (this.currentStep === 'RESULT') {
      this.close();
      return;
    }

    this.submitCode();
  }

  public goBackToPasswordStep(): void {
    if (this.loading) {
      return;
    }

    this.currentStep = 'PASSWORD';
    this.codeErrorMessageKey = null;
    this.codeForm.reset();
  }

  private submitPassword(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.passwordErrorMessageKey = null;

    const opts: MfaInitiateOpts = {
      action: this.isEnableMode ? MfaChallengeAction.ENABLE_MFA : MfaChallengeAction.DISABLE_MFA,
      password: this.passwordForm.get('password')?.value,
    };

    this.mfaHttpService
      .initiateChallenge(opts)
      .pipe(
        take(1),
        tap(response => {
          if (response?.smsSent) {
            this.currentStep = 'CODE';
            this.codeErrorMessageKey = null;
            return;
          }

          this.passwordErrorMessageKey = 'ENABLE_MFA_MODAL.ERRORS.SMS_SEND_FAILED';
        }),
        catchError((error: HttpErrorResponse) => {
          this.passwordErrorMessageKey = this.resolvePasswordErrorMessageKey(error);
          return EMPTY;
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe();
  }

  private submitCode(): void {
    if (this.codeForm.invalid) {
      this.codeForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.codeErrorMessageKey = null;

    const opts: MfaValidateOpts = {
      action: this.isEnableMode ? MfaChallengeAction.ENABLE_MFA : MfaChallengeAction.DISABLE_MFA,
      code: this.codeForm.get('code')?.value,
    };

    this.mfaHttpService
      .validateChallenge(opts)
      .pipe(
        take(1),
        tap(response => {
          if (response?.success) {
            this.showResultStep(this.isEnableMode);
            return;
          }

          this.codeErrorMessageKey = 'ENABLE_MFA_MODAL.ERRORS.INVALID_CODE';
        }),
        catchError((error: HttpErrorResponse) => {
          this.codeErrorMessageKey = this.resolveCodeErrorMessageKey(error);
          return EMPTY;
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe();
  }

  private resolvePasswordErrorMessageKey(error: HttpErrorResponse): string {
    if (error?.status === 429) {
      return 'ENABLE_MFA_MODAL.ERRORS.REQUEST_LIMIT_EXCEEDED';
    }

    if (error?.error?.message === 'INVALID_PASSWORD') {
      return 'ENABLE_MFA_MODAL.ERRORS.INVALID_PASSWORD';
    }

    return 'ENABLE_MFA_MODAL.ERRORS.PASSWORD_CONFIRMATION_FAILED';
  }

  private resolveCodeErrorMessageKey(error: HttpErrorResponse): string {
    if (error?.status === 429) {
      return 'ENABLE_MFA_MODAL.ERRORS.REQUEST_LIMIT_EXCEEDED';
    }

    if (error?.error?.message === 'MFA_VALIDATION_ATTEMPTS_EXCEEDED') {
      return 'ENABLE_MFA_MODAL.ERRORS.ATTEMPTS_EXCEEDED';
    }

    return 'ENABLE_MFA_MODAL.ERRORS.INVALID_CODE';
  }

  private showResultStep(mfaEnabled: boolean): void {
    this.resultMfaEnabled = mfaEnabled;
    this.currentStep = 'RESULT';

    if (mfaEnabled) {
      this.resultTitleKey = 'ENABLE_MFA_MODAL.RESULT.ENABLED.TITLE';
      this.resultDescriptionKey = 'ENABLE_MFA_MODAL.RESULT.ENABLED.DESCRIPTION';
      this.resultSecondaryDescriptionKey = 'ENABLE_MFA_MODAL.RESULT.ENABLED.SECONDARY_DESCRIPTION';
      return;
    }

    this.resultTitleKey = 'ENABLE_MFA_MODAL.RESULT.DISABLED.TITLE';
    this.resultDescriptionKey = 'ENABLE_MFA_MODAL.RESULT.DISABLED.DESCRIPTION';
    this.resultSecondaryDescriptionKey = 'ENABLE_MFA_MODAL.RESULT.DISABLED.SECONDARY_DESCRIPTION';
  }
}
